import { useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    DataGridListEditorProps,
    DataGridListPickerEdit,
} from "../../../columns/listEditors";
import { ListEditTrigger } from "../ListEditTrigger";
import { runListCommit, toCloseAndCommit } from "../listEditActions";
import { type PickerEmptyOption, PickerListOptions } from "./PickerListOptions";

interface PickerListEditorProps<
    TData,
    TOption extends string | number,
> extends DataGridListEditorProps<TData> {
    edit: DataGridListPickerEdit<TData, TOption>;
}

/**
 * `col.edit.picker`: a controlled `Picker.Select` on the cell. The value
 * always comes from the row, so an update landing while it is open shows.
 * The search text is the editor's own; the popup clears it on every close.
 */
export const PickerListEditor = <TData, TOption extends string | number>({
    row,
    trigger,
    isOpen,
    onOpenChange,
    onCloseAndCommit,
    edit,
}: Readonly<PickerListEditorProps<TData, TOption>>) => {
    const [search, setSearch] = useState("");
    const rootProps = {
        open: isOpen,
        onOpenChange,
        searchable: edit.useOptions !== undefined,
        filterable: false,
        autoHighlight: true,
        inputValue: search,
        onInputValueChange: setSearch,
    };
    const renderParts = (emptyOption?: PickerEmptyOption) => (
        <>
            <Picker.Trigger
                render={(triggerProps) => (
                    <ListEditTrigger {...triggerProps} {...trigger} />
                )}
            />
            <Picker.Popup>
                <PickerListOptions
                    row={row}
                    edit={edit}
                    search={search}
                    emptyOption={emptyOption}
                />
            </Picker.Popup>
        </>
    );

    if (edit.multiple === true) {
        return (
            <Picker.Select
                {...rootProps}
                multiple
                value={[...edit.value(row)]}
                onValueChange={(value) => runListCommit({ edit, row, value })}
            >
                {renderParts()}
            </Picker.Select>
        );
    }
    const pick = toCloseAndCommit({ edit, row, onCloseAndCommit });
    return (
        <Picker.Select
            {...rootProps}
            value={edit.value(row)}
            onValueChange={pick}
        >
            {renderParts(
                edit.emptyOption && {
                    label: edit.emptyOption.label,
                    leading: edit.emptyOption.leading,
                    onSelect: () => pick(null),
                },
            )}
        </Picker.Select>
    );
};
